import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  BanknoteArrowUp,
  Building2,
  ChevronRight,
  CircleCheckBig,
  FileCheck2,
  Gauge,
  LineChart,
  MapPinned,
  PhoneCall,
  ShieldCheck,
  Wallet,
  Zap,
} from "lucide-react";

import {
  BRAND_ASSETS,
  HERO_HIGHLIGHTS,
  HOW_IT_WORKS,
  OPERATIONAL_PILLARS,
  SECURITY_POINTS,
  TRUST_NUMBERS,
} from "../data/investorLandingContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultConfig = {
  minimum_investment: 2500,
  founder_spots_total: 10,
  founder_spots_available: 10,
  round_target: 250000,
  activation_threshold: 125000,
  current_raised: 0,
  whatsapp_number: "5522999212644",
  company_name: "MM Service Car",
};

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value || 0));

const formatPhoneDisplay = (phone) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length !== 13) return phone;
  return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
};

const SectionHeading = ({ eyebrow, title, description, testId }) => (
  <div className="max-w-3xl space-y-4" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-400">{eyebrow}</p>
    <h2 className="font-heading text-4xl uppercase leading-none text-white sm:text-5xl" data-testid={`${testId}-title`}>
      {title}
    </h2>
    <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base" data-testid={`${testId}-description`}>
      {description}
    </p>
  </div>
);

const MetricCard = ({ label, value, testId }) => (
  <div className="stat-card" data-testid={testId}>
    <span className="text-xs uppercase tracking-[0.3em] text-slate-400">{label}</span>
    <strong className="mt-3 block font-heading text-3xl uppercase text-white sm:text-4xl" data-testid={`${testId}-value`}>
      {value}
    </strong>
  </div>
);

const iconMap = [BanknoteArrowUp, Wallet, Zap];
const securityIcons = [FileCheck2, Building2, ShieldCheck, MapPinned, CircleCheckBig];
const timelineIcons = [PhoneCall, LineChart, FileCheck2, Gauge];

export default function InvestorLandingPage() {
  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/investor-page/config`);
        setConfig(response.data);
      } catch (error) {
        console.error("Erro ao carregar configurações da landing page", error);
      }
    };

    loadConfig();
  }, []);

  const progressPercentage = Math.min((config.current_raised / config.round_target) * 100, 100);
  const activationPercentage = (config.activation_threshold / config.round_target) * 100;

  const whatsappMessage = encodeURIComponent(
    `Olá! Tenho interesse na oportunidade de investimento da ${config.company_name}. Quero receber mais detalhes sobre a rodada.`
  );
  const whatsappUrl = `https://wa.me/${config.whatsapp_number}?text=${whatsappMessage}`;

  return (
    <main className="page-shell relative overflow-hidden" data-testid="investor-landing-page">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/75 backdrop-blur-xl" data-testid="landing-header">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4" data-testid="brand-block">
            <img alt="Logo MM Service Car" className="h-12 w-auto rounded-sm bg-white/95 p-1" data-testid="brand-logo" src={BRAND_ASSETS.logo} />
            <div>
              <p className="font-heading text-2xl uppercase tracking-[0.18em] text-white" data-testid="brand-name">
                {config.company_name}
              </p>
              <p className="text-xs uppercase tracking-[0.28em] text-slate-400" data-testid="brand-tagline">
                Captação estruturada para expansão automotiva
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-5" data-testid="header-contact-block">
            <div className="hidden text-right sm:block">
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Contato direto</p>
              <p className="text-sm font-semibold text-white" data-testid="header-whatsapp-number">
                {formatPhoneDisplay(config.whatsapp_number)}
              </p>
            </div>
            <a className="cta-button" data-testid="header-whatsapp-button" href={whatsappUrl} rel="noreferrer" target="_blank">
              Quero investir agora <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </header>

      <section
        className="relative isolate min-h-[92vh] overflow-hidden border-b border-white/10"
        data-testid="hero-section"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(2, 6, 23, 0.96) 0%, rgba(2, 6, 23, 0.72) 48%, rgba(2, 6, 23, 0.88) 100%), url(${BRAND_ASSETS.hero})`,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <div className="hero-grid absolute inset-0 opacity-30" />
        <div className="hero-glow absolute inset-0" />

        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-24">
          <div className="space-y-8 self-center">
            <div className="inline-flex items-center gap-3 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-4 py-2" data-testid="hero-live-badge">
              <span className="signal-dot" />
              <span className="text-xs font-semibold uppercase tracking-[0.32em] text-cyan-300">Condições individuais • 10 vagas para esta fase</span>
            </div>

            <div className="max-w-4xl space-y-6">
              <p className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-300" data-testid="hero-eyebrow">
                Oportunidade privada de expansão
              </p>
              <h1 className="font-heading text-3xl uppercase leading-[1.08] tracking-[0.01em] text-white sm:text-4xl lg:text-[3.55rem]" data-testid="hero-title">
                Invista a partir de R$ 2.500 em uma empresa real do setor automotivo
              </h1>
              <div className="hero-trust-line" data-testid="hero-trust-line">
                Empresa real • Operação ativa • Possibilidade de visita • Transparência
              </div>
              <div className="flex flex-col gap-4 sm:flex-row" data-testid="hero-actions">
                <a className="cta-button hero-primary-cta" data-testid="hero-simulate-button" href={whatsappUrl} rel="noreferrer" target="_blank">
                  Quero investir agora <ChevronRight className="h-5 w-5" />
                </a>
                <a className="outline-button" data-testid="hero-contact-button" href="#contato">
                  Solicitar informações <ArrowRight className="h-4 w-4" />
                </a>
              </div>
              <p className="max-w-2xl text-base leading-8 text-slate-200 sm:text-lg" data-testid="hero-subtitle">
                Operação já ativa, com foco em expansão e possibilidade de retorno conforme desempenho real do negócio.
              </p>
            </div>

            <div className="hero-benefit-banner" data-testid="hero-founder-benefit-banner">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-amber-200/80">Benefício do investidor fundador</p>
                <strong className="mt-3 block font-heading text-2xl uppercase text-white sm:text-3xl" data-testid="hero-founder-benefit-text">
                  Condições de rentabilidade definidas no título individual para esta fase da rodada
                </strong>
              </div>
              <div className="hero-urgency-pill" data-testid="hero-urgency-text">
                Vagas limitadas para novos investidores nesta fase de expansão
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <div className="hero-panel w-full overflow-hidden" data-testid="hero-insight-panel">
              <div className="absolute inset-x-[-15%] top-12 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60 animate-scan" />

              <div className="space-y-8 p-6 sm:p-8">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Estrutura da oportunidade</p>
                    <h3 className="font-heading text-3xl uppercase text-white" data-testid="hero-chart-title">
                      Conversa privada e condições individualizadas
                    </h3>
                  </div>
                  <div className="rounded-full border border-cyan-400/20 bg-cyan-400/10 px-4 py-2 text-sm font-semibold text-cyan-300" data-testid="hero-chart-badge">
                    Aporte inicial {formatCurrency(config.minimum_investment)}
                  </div>
                </div>

                <div className="grid gap-4" data-testid="hero-bottom-metrics">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <span className="text-xs uppercase tracking-[0.24em] text-slate-400">Como avançar</span>
                    <strong className="mt-3 block text-2xl font-semibold text-white" data-testid="hero-monthly-return-highlight">
                      Fale direto no WhatsApp para receber os detalhes da operação.
                    </strong>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <span className="text-xs uppercase tracking-[0.24em] text-slate-400">Condições</span>
                    <strong className="mt-3 block text-2xl font-semibold text-white" data-testid="hero-total-return-highlight">
                      Condições de rentabilidade definidas no título individual.
                    </strong>
                  </div>
                  <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5">
                    <span className="text-xs uppercase tracking-[0.24em] text-emerald-200/80">Estrutura real</span>
                    <strong className="mt-3 block text-2xl font-semibold text-emerald-300" data-testid="hero-final-amount-highlight">
                      Empresa em operação, visita possível e transparência na conversa privada.
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-slate-950/60 py-8" data-testid="trust-strip-section">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-3 sm:px-6 lg:px-8">
          {TRUST_NUMBERS.map((item) => (
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-4" data-testid={`trust-strip-${item.label.toLowerCase().replace(/[^a-z0-9]+/gi, "-")}`} key={item.label}>
              <p className="text-xs uppercase tracking-[0.3em] text-slate-500">{item.label}</p>
              <p className="mt-2 text-lg font-semibold text-white">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="simulator-wrapper-section" id="simulador">
        <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <SectionHeading
            description="Em vez de exibir percentuais públicos, esta página direciona a conversa para um contato privado, com mais clareza sobre estrutura, documentos e condições específicas."
            eyebrow="Condições da oportunidade"
            testId="simulator-section-heading"
            title="Converse no privado para entender os detalhes do investimento"
          />

          <div className="glass-panel relative overflow-hidden p-6 sm:p-8" data-testid="investment-simulator-card">
            <div className="absolute inset-x-[-20%] top-10 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-70 animate-scan" />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5" data-testid="simulator-monthly-return-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Aporte inicial</p>
                <strong className="mt-4 block font-heading text-4xl uppercase text-cyan-300" data-testid="simulator-monthly-return-value">
                  {formatCurrency(config.minimum_investment)}
                </strong>
              </div>
              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5" data-testid="simulator-total-return-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Canal principal</p>
                <strong className="mt-4 block font-heading text-4xl uppercase text-white" data-testid="simulator-total-return-value">
                  WhatsApp
                </strong>
              </div>
              <div className="rounded-[1.75rem] border border-emerald-400/20 bg-emerald-400/10 p-5 md:col-span-2" data-testid="simulator-final-amount-card">
                <p className="text-xs uppercase tracking-[0.28em] text-emerald-200/80">Condições da parceria</p>
                <strong className="mt-4 block text-2xl font-semibold text-emerald-300" data-testid="simulator-final-amount-value">
                  Possibilidade de retorno mensal conforme desempenho da operação.
                </strong>
              </div>
              <div className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-5 md:col-span-2" data-testid="simulator-founder-return-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Formalização</p>
                <p className="mt-3 text-lg leading-7 text-slate-200" data-testid="simulator-founder-return-value">
                  As condições de rentabilidade são definidas no título individual e explicadas em conversa privada com a equipe.
                </p>
              </div>
              <a className="cta-button hero-primary-cta md:col-span-2 w-full justify-center" data-testid="simulator-scroll-contact-button" href={whatsappUrl} rel="noreferrer" target="_blank">
                Quero investir agora <PhoneCall className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="founders-progress-section">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="section-shell" data-testid="founders-card">
            <SectionHeading
              description="Vagas limitadas para novos investidores nesta fase de expansão. Os detalhes da parceria são compartilhados em conversa privada, com transparência e formalização individual."
              eyebrow="Fase atual da rodada"
              testId="founders-heading"
              title="Vagas limitadas para novos investidores nesta fase de expansão"
            />

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.75rem] border border-cyan-400/20 bg-cyan-400/10 p-6" data-testid="founders-available-card">
                <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/80">Vagas disponíveis</p>
                <strong className="mt-4 block font-heading text-6xl uppercase text-white" data-testid="founders-available-count">
                  {config.founder_spots_available}
                </strong>
              </div>
              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-6" data-testid="founders-rate-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Formalização</p>
                <strong className="mt-4 block font-heading text-5xl uppercase text-white" data-testid="founders-special-rate">
                  Título individual
                </strong>
              </div>
            </div>

            <div className="ticker-wrap mt-8" data-testid="founders-ticker-wrap">
              <div className="ticker-track">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div className="flex items-center gap-10 px-6" key={`ticker-${index}`}>
                    <span className="text-sm uppercase tracking-[0.32em] text-slate-300">Novos investidores nesta fase</span>
                    <span className="text-sm uppercase tracking-[0.32em] text-cyan-300">Condições definidas no título individual</span>
                    <span className="text-sm uppercase tracking-[0.32em] text-slate-300">Vagas limitadas</span>
                    <span className="text-sm uppercase tracking-[0.32em] text-cyan-300">Contato direto com a empresa</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8" data-testid="middle-whatsapp-cta-wrap">
              <a className="cta-button hero-primary-cta w-full justify-center" data-testid="middle-whatsapp-button" href={whatsappUrl} rel="noreferrer" target="_blank">
                Quero investir agora <PhoneCall className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="section-shell" data-testid="round-progress-card">
            <SectionHeading
              description="A rodada busca capital para ganhar escala operacional e ativar a nova frente voltada ao atendimento de veículos elétricos."
              eyebrow="Progresso da rodada"
              testId="round-progress-heading"
              title="Rodada com ativação planejada a partir de R$ 125 mil"
            />

            <div className="mt-8 space-y-5">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Captação atual</p>
                  <strong className="mt-2 block font-heading text-5xl uppercase text-white" data-testid="round-current-raised">
                    {formatCurrency(config.current_raised)}
                  </strong>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Meta total</p>
                  <strong className="mt-2 block text-2xl font-semibold text-slate-200" data-testid="round-target-raised">
                    {formatCurrency(config.round_target)}
                  </strong>
                </div>
              </div>

              <div className="relative pt-8" data-testid="round-progress-visual">
                <div className="h-5 overflow-hidden rounded-full bg-slate-800/90">
                  <div className="h-full rounded-full bg-gradient-to-r from-blue-600 via-cyan-400 to-emerald-400 shadow-[0_0_20px_rgba(34,211,238,0.35)] transition-[width] duration-700" data-testid="round-progress-fill" style={{ width: `${progressPercentage}%` }} />
                </div>
                <div className="activation-marker" data-testid="round-activation-marker" style={{ left: `${activationPercentage}%` }}>
                  <span>Ativação {formatCurrency(config.activation_threshold)}</span>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <MetricCard label="Meta" testId="round-meta-card" value={formatCurrency(config.round_target)} />
                <MetricCard label="Ponto de ativação" testId="round-activation-card" value={formatCurrency(config.activation_threshold)} />
                <MetricCard label="Percentual atual" testId="round-percent-card" value={`${progressPercentage.toFixed(0)}%`} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="capital-destination-section">
        <SectionHeading
          description="O capital será direcionado para fortalecer a estrutura financeira e operacional da empresa, ampliando capacidade e eficiência para novas frentes de serviço."
          eyebrow="Destinação do capital"
          testId="capital-destination-heading"
          title="Uso planejado do aporte"
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {OPERATIONAL_PILLARS.map((pillar, index) => {
            const Icon = iconMap[index];

            return (
              <article className="section-shell h-full" data-testid={`capital-pillar-card-${index + 1}`} key={pillar.title}>
                <Icon className="h-8 w-8 text-cyan-300" />
                <h3 className="mt-6 font-heading text-3xl uppercase text-white" data-testid={`capital-pillar-title-${index + 1}`}>
                  {pillar.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`capital-pillar-description-${index + 1}`}>
                  {pillar.description}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="security-section">
        <div className="grid items-start gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <SectionHeading
            description="A proposta é apresentada com transparência e ancorada em elementos concretos da operação."
            eyebrow="Segurança"
            testId="security-heading"
            title="Uma oportunidade com base em empresa real e estrutura formal"
          />

          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {SECURITY_POINTS.map((item, index) => {
              const Icon = securityIcons[index];

              return (
                <article className="section-shell min-h-[220px]" data-testid={`security-point-card-${index + 1}`} key={item}>
                  <Icon className="h-8 w-8 text-cyan-300" />
                  <h3 className="mt-6 text-lg font-semibold text-white" data-testid={`security-point-title-${index + 1}`}>
                    {item}
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-slate-300" data-testid={`security-point-description-${index + 1}`}>
                    {item === "Contrato formal"
                      ? "Formalização clara para dar previsibilidade ao relacionamento com investidores."
                      : item === "Empresa real em operação"
                        ? "Estrutura física, operação ativa e atividade econômica vinculada ao setor automotivo."
                        : item === "Ativos como garantia"
                          ? "Base material da operação como elemento adicional de segurança para o investidor."
                          : item === "Possibilidade de visita presencial"
                            ? "O investidor pode conhecer a estrutura e validar de perto a operação."
                            : "Apresentação de indicadores e documentação financeira para suportar a tese."}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="company-proof-section">
        <div className="grid items-start gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="section-shell" data-testid="company-proof-info-card">
            <SectionHeading
              description="Empresa real, com operação ativa, estrutura física e faturamento comprovado."
              eyebrow="Prova de confiança"
              testId="company-proof-heading"
              title="MM Service Car"
            />

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-5" data-testid="company-proof-name-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Empresa</p>
                <strong className="mt-3 block text-2xl font-semibold text-white">MM Service Car</strong>
              </div>
              <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/70 p-5" data-testid="company-proof-cnpj-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">CNPJ</p>
                <strong className="mt-3 block text-2xl font-semibold text-white">29.229.931/0001-48</strong>
              </div>
            </div>

            <div className="mt-6 rounded-[1.75rem] border border-cyan-400/15 bg-gradient-to-br from-cyan-400/10 via-blue-500/10 to-transparent p-6" data-testid="company-proof-description-card">
              <p className="text-sm leading-8 text-slate-200" data-testid="company-proof-description-text">
                Empresa real em operação, possibilidade de visita, estrutura física e transparência em cada etapa da conversa. As condições de rentabilidade são definidas no título individual.
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3" data-testid="company-gallery-grid">
            {BRAND_ASSETS.gallery.map((image, index) => (
              <figure
                className="gallery-card gallery-card-proof"
                data-testid={`company-gallery-image-${index + 1}`}
                key={image}
              >
                <img alt={`Estrutura real da MM Service Car ${index + 1}`} className="h-full w-full object-cover" loading="lazy" src={image} />
                <figcaption className="gallery-caption">
                  {index === 0
                    ? "Vista ampla da oficina e boxes de atendimento"
                    : index === 1
                      ? "Estrutura interna e movimentação de veículos"
                      : "Cabine e área técnica da operação"}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="how-it-works-section">
        <SectionHeading
          description="Fluxo simples, direto e conectado à operação real da empresa."
          eyebrow="Como funciona"
          testId="how-it-works-heading"
          title="Como funciona o investimento"
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          {HOW_IT_WORKS.map((item, index) => {
            const Icon = timelineIcons[index];

            return (
              <article className="section-shell h-full" data-testid={`how-it-works-card-${index + 1}`} key={item.step}>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-5xl uppercase text-slate-600" data-testid={`how-it-works-step-${index + 1}`}>
                    {item.step}
                  </span>
                  <Icon className="h-7 w-7 text-cyan-300" />
                </div>
                <h3 className="mt-6 font-heading text-3xl uppercase text-white" data-testid={`how-it-works-title-${index + 1}`}>
                  {item.title}
                </h3>
                <p className="mt-4 text-sm leading-7 text-slate-300" data-testid={`how-it-works-description-${index + 1}`}>
                  {item.description}
                </p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8" data-testid="contact-section" id="contato">
        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="section-shell h-full" data-testid="contact-info-card">
            <SectionHeading
              description="A conversa detalhada acontece no WhatsApp, onde a equipe apresenta a estrutura da operação, documentação e condições específicas da parceria."
              eyebrow="Canal de contato"
              testId="contact-heading"
              title="Fale com a equipe e receba os detalhes no privado"
            />

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5" data-testid="contact-whatsapp-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">WhatsApp</p>
                <strong className="mt-3 block text-xl font-semibold text-white" data-testid="contact-whatsapp-number">
                  {formatPhoneDisplay(config.whatsapp_number)}
                </strong>
              </div>
              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5" data-testid="contact-minimum-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Aporte mínimo</p>
                <strong className="mt-3 block text-xl font-semibold text-white" data-testid="contact-minimum-value">
                  {formatCurrency(config.minimum_investment)}
                </strong>
              </div>
            </div>

            <div className="mt-6 rounded-[2rem] border border-cyan-400/15 bg-gradient-to-br from-cyan-400/10 via-blue-500/10 to-transparent p-6" data-testid="contact-summary-card">
              <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/80">Resumo da oportunidade</p>
              <p className="mt-3 text-sm leading-7 text-slate-200" data-testid="contact-summary-text">
                Empresa real em operação, fase atual de expansão, possibilidade de visita presencial e condições de rentabilidade definidas no título individual.
              </p>
            </div>
          </div>

          <div className="glass-panel p-6 sm:p-8" data-testid="contact-form-card">
            <div className="space-y-6">
              <div className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-5" data-testid="contact-form-success-box">
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-cyan-200">Contato privado</p>
                <p className="mt-3 text-sm leading-7 text-slate-100" data-testid="contact-form-success-message">
                  Use o WhatsApp como canal principal para receber o racional da operação, documentos, esclarecimentos e condições específicas da parceria.
                </p>
              </div>

              <a className="cta-button hero-primary-cta w-full justify-center" data-testid="contact-form-whatsapp-button" href={whatsappUrl} rel="noreferrer" target="_blank">
                Quero investir agora <PhoneCall className="h-5 w-5" />
              </a>

              <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5" data-testid="contact-private-conditions-card">
                <p className="text-xs uppercase tracking-[0.28em] text-slate-500">Condições</p>
                <p className="mt-3 text-sm leading-7 text-slate-200" data-testid="contact-private-conditions-text">
                  Resultados conforme desempenho da operação e condições de rentabilidade definidas no título individual.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="whatsapp-cta-section">
        <div className="final-whatsapp-cta" data-testid="whatsapp-cta-card">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">Atendimento direto</p>
            <h3 className="mt-3 font-heading text-3xl uppercase text-white sm:text-4xl" data-testid="whatsapp-cta-title">
              Fale diretamente conosco pelo WhatsApp e receba todos os detalhes do investimento.
            </h3>
          </div>
          <a className="cta-button hero-primary-cta" data-testid="whatsapp-cta-button" href={whatsappUrl} rel="noreferrer" target="_blank">
            Quero investir agora <PhoneCall className="h-5 w-5" />
          </a>
        </div>
      </section>

      <footer className="border-t border-white/10 bg-slate-950/90 py-8" data-testid="landing-footer">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-4">
            <img alt="Logo MM Service Car" className="h-12 w-auto rounded-sm bg-white/95 p-1" data-testid="footer-logo" src={BRAND_ASSETS.logo} />
            <div>
              <p className="font-heading text-2xl uppercase text-white" data-testid="footer-company-name">
                {config.company_name}
              </p>
              <p className="text-sm text-slate-400" data-testid="footer-company-description">
                Parceria com empresa em operação real, transparência comercial e expansão da estrutura para veículos elétricos.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 text-sm text-slate-300 lg:items-end">
            <span data-testid="footer-whatsapp-display">WhatsApp: {formatPhoneDisplay(config.whatsapp_number)}</span>
            <a className="text-cyan-300 transition-colors duration-200 hover:text-cyan-200" data-testid="footer-whatsapp-link" href={whatsappUrl} rel="noreferrer" target="_blank">
              Conversar com a equipe comercial
            </a>
          </div>
        </div>
        <div className="mx-auto mt-8 max-w-7xl border-t border-white/10 px-4 pt-5 text-center text-[0.78rem] leading-6 text-slate-400 sm:px-6 lg:px-8" data-testid="footer-legal-disclaimer">
          <p>Esta operação é realizada de forma privada entre as partes, não configurando oferta pública de valores mobiliários.</p>
          <p className="mt-2">Os resultados podem variar conforme o desempenho da operação.</p>
        </div>
      </footer>
    </main>
  );
}